// providers/constants.ts
import * as Api from '@ViewModels';
import type { ComboboxItem, CoreTagOption } from './types';

// ============================================================================
// Configuration Paths - Use these instead of magic strings for autocomplete
// ============================================================================

/**
 * Typed constants for configuration property paths.
 * Use these with `optionsFrom` and `availableFrom` fields for IDE autocomplete.
 *
 * @example
 * ```typescript
 * dynamicTags: ConfigPath.AVAILABLE_FIELDS,
 * customFields: { availableFrom: ConfigPath.AVAILABLE_CUSTOM_FIELDS },
 * ```
 */
export const ConfigPath = {
	/** Standard fields available from the integration */
	AVAILABLE_FIELDS: 'availableFields',
	/** Custom fields defined in the integration */
	AVAILABLE_CUSTOM_FIELDS: 'availableCustomFields',
	/** Parent-account fields available as contact tags (Salesforce Contact record type) */
	AVAILABLE_ACCOUNT_FIELDS: 'availableAccountFields',
	/** Date-type fields available from the integration */
	AVAILABLE_DATE_FIELDS: 'availableDateFields',
	/** Custom date-type fields */
	AVAILABLE_CUSTOM_DATE_FIELDS: 'availableCustomDateFields',
	/** Parent-account date-type fields available as key facts (Salesforce Contact record type) */
	AVAILABLE_ACCOUNT_DATE_FIELDS: 'availableAccountDateFields',
	/** Record types (e.g., Salesforce Contact Record Types) */
	AVAILABLE_RECORD_TYPES: 'availableRecordTypes',
	/** Ownership roles for mapping */
	AVAILABLE_OWNERSHIP_ROLES: 'availableOwnershipRoles',
	/** Writeback types supported by the integration */
	AVAILABLE_WRITEBACK_TYPES: 'availableWriteBackTypes',
	/**
	 * Special: Combines all field sources (fields, customFields, dateFields, customDateFields).
	 * Used for field selectors that need access to all available fields.
	 */
	ALL_AVAILABLE_FIELDS: 'allAvailableFields',
	/** Interest tags available from the integration (e.g., Salesforce Interest Tags) */
	AVAILABLE_INTEREST_TAGS: 'availableInterestTags',
	/** Topics available from the integration (e.g., Salesforce Topics) */
	AVAILABLE_TOPICS: 'availableTopics',
} as const;

/** Type for ConfigPath values */
export type ConfigPathValue = (typeof ConfigPath)[keyof typeof ConfigPath];

// ============================================================================
// Settings Key Paths - Common patterns for storing settings
// ============================================================================

/**
 * Common settings key patterns used across providers.
 * These are the keys where selected values get stored in settings objects.
 */
export const SettingsKey = {
	/** Enabled standard fields (maps to ConfigPath.AVAILABLE_FIELDS) */
	ENABLED_FIELDS: 'enabledFields',
	/** Enabled custom fields (maps to ConfigPath.AVAILABLE_CUSTOM_FIELDS) */
	ENABLED_CUSTOM_FIELDS: 'enabledCustomFields',
	/** Enabled date fields (maps to ConfigPath.AVAILABLE_DATE_FIELDS) */
	ENABLED_DATE_FIELDS: 'enabledDateFields',
	/** Enabled custom date fields (maps to ConfigPath.AVAILABLE_CUSTOM_DATE_FIELDS) */
	ENABLED_CUSTOM_DATE_FIELDS: 'enabledCustomDateFields',
} as const;

/** Type for SettingsKey values */
export type SettingsKeyValue = (typeof SettingsKey)[keyof typeof SettingsKey];

// ============================================================================
// Core Tags
// ============================================================================

/**
 * Core location tags available to most providers.
 * These map to the base ITagSettings properties.
 */
export const CORE_TAGS: CoreTagOption[] = [
	{ id: 'State', label: 'State', settingsKey: 'stateEnabled' },
	{ id: 'City', label: 'City', settingsKey: 'cityEnabled' },
	{ id: 'PostalCode', label: 'Postal Code', settingsKey: 'postalCodeEnabled' },
	{ id: 'Country', label: 'Country', settingsKey: 'countryEnabled' },
];

// ============================================================================
// Shared Select Options
// ============================================================================

/** Attorney tag options shared by MyCase and Clio providers */
export const ATTORNEY_TAG_OPTIONS: ComboboxItem[] = [
	{ id: Api.AttorneyTagOption.None, label: 'None' },
	{ id: Api.AttorneyTagOption.MostRecent, label: 'Most Recent' },
	{ id: Api.AttorneyTagOption.All, label: 'All' },
];
